import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AuthService } from '../services/utils/auth.service';
import { ProfessorService } from '../services/professor.service';
import { StudentService } from '../services/student.service';

/** Rôles métier dont l'accès suppose une fiche en base. */
type LinkedRole = 'professor' | 'student';

/**
 * Vérifie qu'un compte Keycloak est bien rattaché à sa fiche métier.
 *
 * <p>Le back dérive les claims `id_professor` / `id_student` de la
 * correspondance entre le `sub` du jeton et la table `professors` /
 * `students`. Un compte qui porte le rôle sans avoir de fiche obtient donc un
 * jeton valide mais amputé : l'application se charge, et <em>chaque</em> appel
 * répond 401 « Identifiant professor introuvable dans l'identité token ». Sans
 * ce garde, l'utilisateur n'a qu'une cascade d'erreurs muettes.</p>
 *
 * <p>Le claim étant ajouté côté serveur, il est absent du jeton que détient le
 * front : impossible de trancher en local. On interroge donc l'endpoint
 * `by-user`, qui lui ne réclame pas le claim. Une seule requête, mise en cache
 * pour la durée de la session.</p>
 */
@Injectable({ providedIn: 'root' })
export class AccountLinkGuard implements CanActivate {
  private readonly auth = inject(AuthService);
  private readonly professors = inject(ProfessorService);
  private readonly students = inject(StudentService);
  private readonly router = inject(Router);

  /** Résultat déjà établi pour cette session, par rôle. */
  private readonly checked = new Map<LinkedRole, boolean>();

  async canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    const roles = (route.data['roles'] as string[] | undefined) ?? [];
    const role: LinkedRole | null = roles.includes('professor')
      ? 'professor'
      : roles.includes('student')
        ? 'student'
        : null;

    // Le superviseur n'a pas de fiche métier : rien à vérifier.
    if (!role) return true;

    const known = this.checked.get(role);
    if (known !== undefined) {
      return known ? true : this.unlinked(role);
    }

    const userId = await this.auth.getUserId();
    if (!userId) return true; // Pas d'identité exploitable : RoleGuard tranchera.

    const linked = await this.hasProfile(role, userId);
    this.checked.set(role, linked);
    return linked ? true : this.unlinked(role);
  }

  /**
   * Une erreur réseau ne doit pas être lue comme « compte non rattaché » :
   * en cas de doute on laisse passer, et l'utilisateur verra les erreurs
   * réelles plutôt qu'un diagnostic inventé.
   */
  private hasProfile(role: LinkedRole, userId: string): Promise<boolean> {
    // Typage volontairement minimal : les deux services renvoient des
    // enveloppes de formes différentes, seule la présence de `data` compte ici.
    const request$: Observable<{ data?: unknown } | null> =
      role === 'professor'
        ? this.professors.getByUserId(userId)
        : this.students.getStudentByUserId(userId);

    return firstValueFrom(
      request$.pipe(
        map((res) => !!res?.data),
        catchError((err: unknown) =>
          of((err as { status?: number })?.status !== 404),
        ),
      ),
    );
  }

  private unlinked(role: LinkedRole): UrlTree {
    return this.router.createUrlTree(['/account-unlinked'], {
      queryParams: { role },
    });
  }

  /** À appeler après un rattachement, pour forcer une nouvelle vérification. */
  reset(): void {
    this.checked.clear();
  }
}
